import React, { useState, useEffect } from 'react';
import { ArrowRight, ArrowUpRight, Star, Quote, Truck, RefreshCw, Shield, Clock, Play, ChevronLeft, ChevronRight, Instagram, Heart, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * Template 7: VOGUE
 * Style: High Fashion, Bold, Statement-making
 * Best for: Designer wear, Couture, Premium Indian fashion brands
 * Light theme with bold rose/blush accents
 */
const Template7 = () => {
    const navigate = useNavigate();
    const [currentSlide, setCurrentSlide] = useState(0);
    const [currentLook, setCurrentLook] = useState(0);

    // Hero slides - Fashion campaigns
    const heroSlides = [
        {
            title: 'Haute Couture',
            subtitle: 'Spring/Summer 2024',
            description: 'Where Indian artistry meets global luxury',
            image: 'https://placehold.co/1920x1080/FDF2F8/831843?text=Couture+Campaign',
            cta: 'View Collection'
        },
        {
            title: 'The Bridal Edit',
            subtitle: 'Exclusive Preview',
            description: 'Timeless elegance for your special day',
            image: 'https://placehold.co/1920x1080/FFF1F2/881337?text=Bridal+Campaign',
            cta: 'Explore Bridal'
        },
        {
            title: 'Festive Luxe',
            subtitle: 'Limited Edition',
            description: 'Celebrate in extraordinary style',
            image: 'https://placehold.co/1920x1080/FCE7F3/9D174D?text=Festive+Campaign',
            cta: 'Shop Festive'
        }
    ];

    // Auto-slide
    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
        }, 5000);
        return () => clearInterval(timer);
    }, []);

    // Categories - High fashion focus
    const categories = [
        { name: 'Designer Sarees', description: 'Exclusive Weaves', image: 'https://placehold.co/500x700/FDF2F8/831843?text=Sarees', count: '200+' },
        { name: 'Bridal Lehengas', description: 'Couture Collection', image: 'https://placehold.co/500x700/FFF1F2/881337?text=Lehengas', count: '150+' },
        { name: 'Gowns', description: 'Red Carpet Ready', image: 'https://placehold.co/500x700/FCE7F3/9D174D?text=Gowns', count: '80+' },
        { name: 'Anarkalis', description: 'Statement Pieces', image: 'https://placehold.co/500x700/FDF2F8/831843?text=Anarkalis', count: '120+' }
    ];

    // Featured products - Premium Indian fashion
    const products = [
        { id: 1, name: 'Hand-embroidered Silk Lehenga', designer: 'By Ritu Kumar', price: '₹1,25,000', image: 'https://placehold.co/400x550/FDF2F8/831843?text=Lehenga+1', isNew: true },
        { id: 2, name: 'Banarasi Tissue Saree', designer: 'By Sabyasachi', price: '₹85,000', image: 'https://placehold.co/400x550/FFF1F2/881337?text=Saree+1', isNew: false },
        { id: 3, name: 'Zardozi Work Anarkali', designer: 'By Manish Malhotra', price: '₹78,500', image: 'https://placehold.co/400x550/FCE7F3/9D174D?text=Anarkali+1', isNew: true },
        { id: 4, name: 'Crystal Embellished Gown', designer: 'By Tarun Tahiliani', price: '₹1,45,000', image: 'https://placehold.co/400x550/FDF2F8/831843?text=Gown+1', isNew: false },
        { id: 5, name: 'Kalamkari Printed Lehenga', designer: 'By Anita Dongre', price: '₹92,000', image: 'https://placehold.co/400x550/FFF1F2/881337?text=Lehenga+2', isNew: true },
        { id: 6, name: 'Chikankari Palazzo Set', designer: 'By Abu Jani Sandeep', price: '₹65,000', image: 'https://placehold.co/400x550/FCE7F3/9D174D?text=Palazzo+1', isNew: false }
    ];

    // Lookbook images
    const lookbook = [
        { image: 'https://placehold.co/600x900/FDF2F8/831843?text=Look+1', title: 'The Modern Maharani' },
        { image: 'https://placehold.co/600x900/FFF1F2/881337?text=Look+2', title: 'Festive Opulence' },
        { image: 'https://placehold.co/600x900/FCE7F3/9D174D?text=Look+3', title: 'Evening Glamour' },
        { image: 'https://placehold.co/600x900/FDF2F8/831843?text=Look+4', title: 'Bridal Dreams' }
    ];

    // Testimonials
    const testimonials = [
        { text: 'The craftsmanship of my bridal lehenga was beyond extraordinary. Every detail was perfect for my Mumbai wedding.', name: 'Aishwarya Mehta', location: 'Mumbai', event: 'Bride, Dec 2024' },
        { text: 'As a celebrity stylist, I only recommend the best to my clients. This collection sets the standard for Indian luxury fashion.', name: 'Rhea Kapoor', location: 'Delhi', event: 'Celebrity Stylist' },
        { text: 'The fusion of traditional techniques with contemporary design is remarkable. Truly wearable art.', name: 'Deepika Singhania', location: 'Jaipur', event: 'Fashion Collector' }
    ];

    // Designer spotlight
    const designers = [
        { name: 'Sabyasachi Mukherjee', specialty: 'Bridal Couture', image: 'https://placehold.co/200x200/FDF2F8/831843?text=S' },
        { name: 'Manish Malhotra', specialty: 'Celebrity Fashion', image: 'https://placehold.co/200x200/FFF1F2/881337?text=M' },
        { name: 'Anita Dongre', specialty: 'Sustainable Luxury', image: 'https://placehold.co/200x200/FCE7F3/9D174D?text=A' },
        { name: 'Ritu Kumar', specialty: 'Heritage Textiles', image: 'https://placehold.co/200x200/FDF2F8/831843?text=R' }
    ];

    return (
        <div className="w-full min-h-screen bg-white font-sans">
            {/* Hero Section - Full Screen Slider */}
            <section className="relative h-screen overflow-hidden">
                {heroSlides.map((slide, index) => (
                    <div
                        key={index}
                        className={`absolute inset-0 transition-opacity duration-1000 ${
                            currentSlide === index ? 'opacity-100 z-10' : 'opacity-0 z-0'
                        }`}
                    >
                        <img
                            src={slide.image}
                            alt={slide.title}
                            className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-r from-rose-950/60 via-rose-900/30 to-transparent" />

                        <div className="absolute inset-0 flex items-center">
                            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
                                <div className="max-w-2xl">
                                    <span className="inline-block text-rose-200 text-sm font-medium tracking-[0.3em] uppercase mb-4 animate-fade-in">
                                        {slide.subtitle}
                                    </span>
                                    <h1 className="text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-extralight text-white leading-[0.9] mb-6 tracking-tight">
                                        {slide.title.split(' ').map((word, i) => (
                                            <span key={i} className={i === 1 ? 'font-serif italic block' : 'block'}>
                                                {word}
                                            </span>
                                        ))}
                                    </h1>
                                    <p className="text-rose-100 text-lg lg:text-xl mb-8 max-w-md leading-relaxed">
                                        {slide.description}
                                    </p>
                                    <button
                                        onClick={() => navigate('/template7/shop')}
                                        className="group inline-flex items-center gap-3 px-8 py-4 bg-white text-rose-900 text-sm font-semibold tracking-wider uppercase hover:bg-rose-50 transition-all duration-300"
                                    >
                                        {slide.cta}
                                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}

                {/* Slide Navigation */}
                <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20 flex gap-3">
                    {heroSlides.map((_, index) => (
                        <button
                            key={index}
                            onClick={() => setCurrentSlide(index)}
                            className={`h-1 rounded-full transition-all duration-300 ${
                                currentSlide === index ? 'w-12 bg-white' : 'w-6 bg-white/40 hover:bg-white/60'
                            }`}
                        />
                    ))}
                </div>

                {/* Slide Arrows */}
                <button
                    onClick={() => setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length)}
                    className="absolute left-4 lg:left-8 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-all"
                >
                    <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                    onClick={() => setCurrentSlide((prev) => (prev + 1) % heroSlides.length)}
                    className="absolute right-4 lg:right-8 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-all"
                >
                    <ChevronRight className="w-5 h-5" />
                </button>
            </section>

            {/* Brand Statement */}
            <section className="py-20 lg:py-28 bg-gradient-to-b from-rose-50/50 to-white">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <Sparkles className="w-8 h-8 text-rose-400 mx-auto mb-6" />
                    <h2 className="text-3xl lg:text-4xl xl:text-5xl font-extralight text-rose-950 leading-relaxed mb-6">
                        "Fashion is not just about clothes. It's about <span className="font-serif italic text-rose-600">heritage</span>,
                        <span className="font-serif italic text-rose-600"> craftsmanship</span>, and the
                        <span className="font-serif italic text-rose-600"> story</span> each piece tells."
                    </h2>
                    <p className="text-rose-700/70 text-lg max-w-2xl mx-auto">
                        Curating India's finest designers and artisans for the discerning fashion connoisseur
                    </p>
                </div>
            </section>

            {/* Categories - Magazine Grid */}
            <section className="py-16 lg:py-24 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-12 lg:mb-16">
                        <div>
                            <span className="text-rose-500 text-sm font-medium tracking-[0.2em] uppercase">The Collections</span>
                            <h2 className="text-3xl lg:text-5xl font-extralight text-rose-950 mt-3">Shop By Category</h2>
                        </div>
                        <button
                            onClick={() => navigate('/template7/shop')}
                            className="group flex items-center gap-2 text-rose-700 font-medium mt-4 lg:mt-0 hover:text-rose-900 transition-colors"
                        >
                            View All
                            <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                        </button>
                    </div>

                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
                        {categories.map((category, index) => (
                            <div
                                key={index}
                                onClick={() => navigate('/template7/shop')}
                                className="group cursor-pointer relative overflow-hidden"
                            >
                                <div className="aspect-[5/7] overflow-hidden">
                                    <img
                                        src={category.image}
                                        alt={category.name}
                                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                                    />
                                </div>
                                <div className="absolute inset-0 bg-gradient-to-t from-rose-950/80 via-rose-900/20 to-transparent" />
                                <div className="absolute bottom-0 left-0 right-0 p-4 lg:p-6">
                                    <p className="text-rose-200 text-xs tracking-wider uppercase mb-1">{category.description}</p>
                                    <h3 className="text-white text-lg lg:text-xl font-medium">{category.name}</h3>
                                    <p className="text-rose-300 text-sm mt-2">{category.count} Styles</p>
                                </div>
                                <div className="absolute top-4 right-4 w-10 h-10 bg-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 transform scale-75 group-hover:scale-100">
                                    <ArrowUpRight className="w-4 h-4 text-rose-900" />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Designer Spotlight */}
            <section className="py-16 lg:py-24 bg-rose-50/50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12 lg:mb-16">
                        <span className="text-rose-500 text-sm font-medium tracking-[0.2em] uppercase">Featured Designers</span>
                        <h2 className="text-3xl lg:text-4xl font-extralight text-rose-950 mt-3">India's Finest Couturiers</h2>
                    </div>

                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
                        {designers.map((designer, index) => (
                            <div
                                key={index}
                                className="group text-center cursor-pointer"
                                onClick={() => navigate('/template7/shop')}
                            >
                                <div className="relative w-32 h-32 lg:w-40 lg:h-40 mx-auto mb-4 overflow-hidden rounded-full bg-white shadow-lg group-hover:shadow-xl transition-shadow">
                                    <img
                                        src={designer.image}
                                        alt={designer.name}
                                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                    />
                                </div>
                                <h3 className="text-rose-950 font-medium text-lg group-hover:text-rose-600 transition-colors">
                                    {designer.name}
                                </h3>
                                <p className="text-rose-600/70 text-sm mt-1">{designer.specialty}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Featured Products */}
            <section className="py-16 lg:py-24 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-12 lg:mb-16">
                        <div>
                            <span className="text-rose-500 text-sm font-medium tracking-[0.2em] uppercase">The Edit</span>
                            <h2 className="text-3xl lg:text-5xl font-extralight text-rose-950 mt-3">New Arrivals</h2>
                        </div>
                        <button
                            onClick={() => navigate('/template7/shop')}
                            className="group flex items-center gap-2 text-rose-700 font-medium mt-4 lg:mt-0 hover:text-rose-900 transition-colors"
                        >
                            Shop All New
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </button>
                    </div>

                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-8">
                        {products.map((product) => (
                            <div
                                key={product.id}
                                onClick={() => navigate('/template7/shop')}
                                className="group cursor-pointer"
                            >
                                <div className="relative aspect-[4/5.5] overflow-hidden bg-rose-50 mb-4">
                                    <img
                                        src={product.image}
                                        alt={product.name}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                    />
                                    {product.isNew && (
                                        <span className="absolute top-4 left-4 px-3 py-1 bg-rose-600 text-white text-xs font-medium tracking-wider uppercase">
                                            New
                                        </span>
                                    )}
                                    <button className="absolute top-4 right-4 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 hover:bg-white hover:text-rose-600">
                                        <Heart className="w-5 h-5" />
                                    </button>
                                    <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-rose-950/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                        <button className="w-full py-3 bg-white text-rose-900 text-sm font-semibold uppercase tracking-wider hover:bg-rose-50 transition-colors">
                                            Quick View
                                        </button>
                                    </div>
                                </div>
                                <div className="space-y-1">
                                    <p className="text-rose-500 text-xs tracking-wider uppercase">{product.designer}</p>
                                    <h3 className="text-rose-950 font-medium group-hover:text-rose-600 transition-colors line-clamp-1">
                                        {product.name}
                                    </h3>
                                    <p className="text-rose-900 font-semibold text-lg">{product.price}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Lookbook Section */}
            <section className="py-16 lg:py-24 bg-rose-950 text-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-12 lg:mb-16">
                        <div>
                            <span className="text-rose-300 text-sm font-medium tracking-[0.2em] uppercase">Style Inspiration</span>
                            <h2 className="text-3xl lg:text-5xl font-extralight text-white mt-3">The Lookbook</h2>
                        </div>
                        <button className="group flex items-center gap-2 text-rose-300 font-medium mt-4 lg:mt-0 hover:text-white transition-colors">
                            View Full Lookbook
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </button>
                    </div>

                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
                        {lookbook.map((look, index) => (
                            <div
                                key={index}
                                className="group relative aspect-[2/3] overflow-hidden cursor-pointer"
                            >
                                <img
                                    src={look.image}
                                    alt={look.title}
                                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-rose-950/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                                <div className="absolute bottom-0 left-0 right-0 p-4 transform translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                                    <p className="text-white text-sm font-medium">{look.title}</p>
                                </div>
                                <div className="absolute top-4 right-4 w-10 h-10 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300">
                                    <Play className="w-4 h-4 text-white ml-0.5" />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Testimonials */}
            <section className="py-16 lg:py-24 bg-gradient-to-b from-white to-rose-50/50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12 lg:mb-16">
                        <span className="text-rose-500 text-sm font-medium tracking-[0.2em] uppercase">Client Stories</span>
                        <h2 className="text-3xl lg:text-4xl font-extralight text-rose-950 mt-3">Voices of Elegance</h2>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
                        {testimonials.map((testimonial, index) => (
                            <div key={index} className="bg-white p-8 lg:p-10 shadow-sm hover:shadow-lg transition-shadow duration-300 border-t-2 border-rose-200">
                                <Quote className="w-10 h-10 text-rose-200 mb-6" />
                                <p className="text-rose-800 leading-relaxed mb-8 text-lg italic">
                                    "{testimonial.text}"
                                </p>
                                <div className="flex items-center gap-4">
                                    <div className="w-14 h-14 bg-rose-100 rounded-full flex items-center justify-center">
                                        <span className="text-rose-600 font-semibold text-lg">{testimonial.name[0]}</span>
                                    </div>
                                    <div>
                                        <p className="font-medium text-rose-950">{testimonial.name}</p>
                                        <p className="text-rose-600/70 text-sm">{testimonial.event}</p>
                                        <p className="text-rose-400 text-xs">{testimonial.location}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Brand Story Section */}
            <section className="py-16 lg:py-24 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
                        <div className="relative">
                            <div className="aspect-[4/5] overflow-hidden">
                                <img
                                    src="https://placehold.co/600x750/FDF2F8/831843?text=Atelier"
                                    alt="Fashion Atelier"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            <div className="absolute -bottom-8 -right-8 w-48 h-48 bg-rose-100 -z-10" />
                            <div className="absolute top-8 -left-8 w-32 h-32 border-2 border-rose-200 -z-10" />
                        </div>
                        <div>
                            <span className="text-rose-500 text-sm font-medium tracking-[0.2em] uppercase">Our Story</span>
                            <h2 className="text-3xl lg:text-4xl font-extralight text-rose-950 mt-3 mb-6">
                                Where Tradition Meets <span className="font-serif italic">Modernity</span>
                            </h2>
                            <p className="text-rose-700/80 text-lg leading-relaxed mb-6">
                                Founded in Mumbai in 2010, VOGUE has been at the forefront of redefining Indian luxury fashion.
                                We curate the finest designer collections, working directly with master artisans and couturiers
                                who pour their heart and soul into every stitch.
                            </p>
                            <p className="text-rose-700/80 leading-relaxed mb-8">
                                From bridal trousseau to red carpet moments, we dress India's most discerning fashion connoisseurs.
                                Each piece in our collection carries the legacy of Indian craftsmanship, reimagined for the modern woman.
                            </p>
                            <div className="grid grid-cols-3 gap-8 mb-8 py-8 border-y border-rose-100">
                                <div className="text-center">
                                    <p className="text-3xl lg:text-4xl font-light text-rose-600">50+</p>
                                    <p className="text-rose-700/70 text-sm mt-1">Designers</p>
                                </div>
                                <div className="text-center">
                                    <p className="text-3xl lg:text-4xl font-light text-rose-600">10k+</p>
                                    <p className="text-rose-700/70 text-sm mt-1">Brides Dressed</p>
                                </div>
                                <div className="text-center">
                                    <p className="text-3xl lg:text-4xl font-light text-rose-600">15</p>
                                    <p className="text-rose-700/70 text-sm mt-1">Years of Excellence</p>
                                </div>
                            </div>
                            <button
                                onClick={() => navigate('/template7/shop')}
                                className="group inline-flex items-center gap-3 text-rose-700 font-medium hover:text-rose-900 transition-colors"
                            >
                                Learn More About Us
                                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Newsletter */}
            <section className="py-16 lg:py-24 bg-gradient-to-br from-rose-100 via-rose-50 to-pink-50">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <Sparkles className="w-8 h-8 text-rose-400 mx-auto mb-6" />
                    <h2 className="text-3xl lg:text-4xl font-extralight text-rose-950 mb-4">
                        Join the Inner Circle
                    </h2>
                    <p className="text-rose-700/80 text-lg mb-8">
                        Be the first to access exclusive collections, private sales, and fashion events.
                        Plus, enjoy 10% off your first purchase.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 max-w-lg mx-auto">
                        <input
                            type="email"
                            placeholder="Enter your email"
                            className="flex-1 px-6 py-4 bg-white border border-rose-200 text-rose-900 placeholder-rose-400 focus:outline-none focus:border-rose-400 transition-colors"
                        />
                        <button className="px-8 py-4 bg-rose-600 text-white text-sm font-semibold tracking-wider uppercase hover:bg-rose-700 transition-colors whitespace-nowrap">
                            Subscribe
                        </button>
                    </div>
                    <p className="text-rose-500/70 text-sm mt-4">
                        Exclusive access. No spam. Unsubscribe anytime.
                    </p>
                </div>
            </section>

            {/* Instagram Feed */}
            <section className="py-16 lg:py-24 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <div className="flex items-center justify-center gap-3 mb-3">
                            <Instagram className="w-6 h-6 text-rose-600" />
                            <span className="text-rose-600 font-medium">@voguefashionindia</span>
                        </div>
                        <h2 className="text-3xl lg:text-4xl font-extralight text-rose-950">Follow Our World</h2>
                    </div>

                    <div className="grid grid-cols-3 md:grid-cols-6 gap-2 lg:gap-4">
                        {[1, 2, 3, 4, 5, 6].map((_, index) => (
                            <div key={index} className="group relative aspect-square overflow-hidden cursor-pointer">
                                <img
                                    src={`https://placehold.co/300x300/FDF2F8/831843?text=Insta+${index + 1}`}
                                    alt={`Instagram post ${index + 1}`}
                                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                />
                                <div className="absolute inset-0 bg-rose-600/0 group-hover:bg-rose-600/30 transition-colors duration-300 flex items-center justify-center">
                                    <Heart className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Services */}
            <section className="py-12 bg-rose-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm">
                                <Truck className="w-5 h-5 text-rose-600" />
                            </div>
                            <div>
                                <p className="font-medium text-rose-950">Complimentary Shipping</p>
                                <p className="text-rose-600/70 text-sm">On orders above ₹25,000</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm">
                                <RefreshCw className="w-5 h-5 text-rose-600" />
                            </div>
                            <div>
                                <p className="font-medium text-rose-950">Hassle-free Returns</p>
                                <p className="text-rose-600/70 text-sm">Within 7 days</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm">
                                <Shield className="w-5 h-5 text-rose-600" />
                            </div>
                            <div>
                                <p className="font-medium text-rose-950">Authenticity Guaranteed</p>
                                <p className="text-rose-600/70 text-sm">100% genuine designers</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm">
                                <Clock className="w-5 h-5 text-rose-600" />
                            </div>
                            <div>
                                <p className="font-medium text-rose-950">Personal Styling</p>
                                <p className="text-rose-600/70 text-sm">Expert consultation</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Template7;
